import {FC, useCallback, useEffect, useState} from 'react'
import {
  Automation,
  AutomationPipeline,
  AutomationPipelineType,
} from '../../../../../../services/cellcast.v2.types'
import {KTSVG} from '../../../../../../_metronic/helpers'
import {Alert, Spinner} from 'react-bootstrap'
import WSButton from '../../../../../../_metronic/helpers/components/WSButton/WSButton'
import './InputPipeline.scss'
import Select from 'react-select'
import {SelectOption} from '../../../../../types'
import {WriteMultiStepType} from '../write'
import MessageBoxV2 from '../../../../../Components/MessageBoxV2'
import {useLazyGetSendersQuery} from '../../../../../../services/cellcast.v2'

type InputPipelineProps = {
  mode: string
  currentStep: WriteMultiStepType
  data: Automation
  tagOptions: SelectOption<string>[]
  setData: (automation: Automation) => void
  error: string
  isLoading: boolean
  handleClose: () => void
  handleBack: () => void
  handleNext: () => void
}

const InputPipeline: FC<InputPipelineProps> = (props) => {
  const {
    currentStep,
    isLoading,
    data,
    mode,
    error,
    tagOptions,
    setData,
    handleClose,
    handleBack,
    handleNext,
  } = props

  const [pipelines, setPipelines] = useState<AutomationPipeline[]>([])
  const [senderOptions, setSenderOptions] = useState<SelectOption<string>[]>([
    {value: '#SharedNum#', label: '#SharedNum#'},
  ])
  const [inputError, setInputError] = useState<Record<number, Record<string, any>>>({})

  const [
    fetchSender,
    {data: sendersResponse, isFetching: fetchSenderIsFetching, isLoading: fetchSenderIsLoading},
  ] = useLazyGetSendersQuery()

  const handleInputChange = (index: number, key: string, newValue: any) => {
    let updatedSteps = [...pipelines] as any
    let updateData = {...updatedSteps[index]}
    updateData[key] = newValue
    updatedSteps[index] = updateData

    if (key === 'type' && newValue === AutomationPipelineType.addTag) {
    }
    setPipelines(updatedSteps)
    setInputError({})
  }

  const handleInputConfigChange = (index: number, key: string, newValue: any) => {
    let updatedSteps = [...pipelines]
    let updateData = {...updatedSteps[index]}

    if (!updateData.config) {
      updateData.config = {}
    }

    let tmpConfig = {...updateData.config}
    if (key === 'recipients') {
      newValue = newValue.split(',')
    }
    tmpConfig[key] = newValue

    if (key === 'to') {
      tmpConfig['recipients'] = []
    }
    updateData.config = tmpConfig
    updatedSteps[index] = updateData

    setPipelines(updatedSteps)
    setInputError({})
  }

  const removeByIndex = (index: number) => {
    const tmp = pipelines.filter((_, elIndex) => elIndex !== index)
    setPipelines(tmp)
  }

  const handleAddOne = () => {
    const tmp = [...pipelines]
    tmp.push({
      type: AutomationPipelineType.addTag,
      config: {},
    })
    setPipelines(tmp)
  }

  const validatePipeline = (data: AutomationPipeline[]) => {
    const inputError = {} as Record<number, any>

    for (let i = 0; i < data.length; i++) {
      const item = {...data[i]}
      const err = {} as Record<string, any>
      if (item.type.toString() === '') {
        err['type'] = 'type is required'
      }

      if (item.type === AutomationPipelineType.addTag) {
        if (!item.config) {
          err['tagIds'] = 'tagIds is required'
        } else if (item.config && !item.config.tagIds) {
          err['tagIds'] = 'tagIds is required'
        } else if (item.config && item.config.tagIds && !Array.isArray(item.config.tagIds)) {
          err['tagIds'] = 'config tagIds must be valid array'
        } else if (
          item.config &&
          item.config.tagIds &&
          Array.isArray(item.config.tagIds) &&
          item.config.tagIds.length === 0
        ) {
          err['tagIds'] = 'tagIds is required'
        }
      }

      if (item.type === AutomationPipelineType.forwardReply) {
        if (!item.config) {
          err['email'] = 'email is required'
        } else if (item.config && !item.config.email) {
          err['email'] = 'email is required'
        } else if (item.config && item.config.email.length === 0) {
          err['email'] = 'email is required'
        }
      }

      if (item.type === AutomationPipelineType.sendSms) {
        if (!item.config) {
          err['config'] = 'config is required'
        }

        if (item.config && !item.config.to) {
          err['to'] = 'target type is required'
        }

        if (item.config && !Array.isArray(item.config.recipients)) {
          err['recipients'] = 'recipients must be valid array'
        } else if (
          item.config &&
          !Array.isArray(item.config.recipients) &&
          item.config.recipients.length === 0
        ) {
          err['recipients'] = 'recipients can`t be empty'
        }

        if (
          item.config &&
          (!item.config.message || (item.config.message && item.config.message.length === 0))
        ) {
          err['message'] = 'message type is required'
        }
      }

      if (Object.keys(err).length > 0) {
        inputError[i] = err
      }
    }

    if (Object.keys(inputError).length > 0) {
      return {
        isError: true,
        error: inputError,
      }
    }

    return {
      isError: false,
      error: {},
    }
  }

  const handleLocalNext = useCallback(() => {
    const result = validatePipeline(pipelines)
    if (result.isError) {
      setInputError(result.error)
      return
    }
    const newPipeline = pipelines.map((el, i) => ({...el, order: i + 1}))
    setData({...data, pipelines: newPipeline})
    handleNext()
  }, [pipelines])

  useEffect(() => {
    if (data.pipelines) {
      setPipelines(data.pipelines)
    } else {
      setPipelines([])
    }
  }, [data.pipelines])

  useEffect(() => {
    if (currentStep === WriteMultiStepType.InputPipeline) {
      fetchSender(null)
    }
  }, [currentStep])

  useEffect(() => {
    if (sendersResponse && sendersResponse.data) {
      const smsOptions = sendersResponse?.data?.sms.filter((el) => el.value !== '#SharedNum#') || []
      const mmsOptions = sendersResponse?.data?.mms.filter((el) => el.value !== '#SharedNum#') || []
      const senderOptions = [
        {value: '#SharedNum#', label: '#SharedNum#'},
        ...smsOptions,
        ...mmsOptions,
      ]
      setSenderOptions(senderOptions)
    }
  }, [sendersResponse])

  return (
    <>
      <div className='modal-header'>
        <h2 className='fs-2'>{data._id ? 'Update' : 'Create'} Multi Step Automation</h2>
        <div
          className='btn btn-icon btn-sm'
          onClick={() => {
            handleClose()
          }}
        >
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>
      <div className='modal-body py-lg-5 px-lg-5 border-bottom'>
        {error.length > 0 && (
          <Alert variant='danger'>
            <Alert.Heading>Error</Alert.Heading>
            <p>{error}</p>
          </Alert>
        )}

        <div className='pipeline'>
          {pipelines?.map((el, index) => (
            <>
              <PipelineItem
                key={index}
                automation={data}
                tagOptions={tagOptions}
                senderOptions={senderOptions}
                inputError={inputError[index] || {}}
                index={index}
                handleChange={handleInputChange}
                handleChangeConfig={handleInputConfigChange}
                removeByIndex={removeByIndex}
                data={el}
              />
              <div className='vertical-line'></div>
              <div className='vertical-line'></div>
              <div className='vertical-line'></div>
            </>
          ))}
        </div>
        <div className='pipeline-button mt-5'>
          <button
            className='btn btn-sm btn-primary'
            style={{verticalAlign: 'bottom'}}
            onClick={() => handleAddOne()}
          >
            <i className='bi bi-plus fs-3'></i> Add Other
          </button>
        </div>

        <div className='container d-flex align-items-end justify-content-end mt-6 mb-5'>
          <WSButton
            type='submit'
            className='btn btn-lg btn-light-primary me-5 fw-500'
            onClick={() => handleBack()}
            disabled={isLoading}
          >
            <span className='indicator-label'>back</span>
          </WSButton>
          <WSButton
            type='submit'
            className='btn btn-lg btn-primary fw-500'
            onClick={() => handleLocalNext()}
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner animation='grow' />
            ) : (
              <span className='indicator-label'>{mode === 'create' ? 'Create' : 'Update'}</span>
            )}
          </WSButton>
        </div>
      </div>
    </>
  )
}

// Pipeline Items
const pipelineType = [
  AutomationPipelineType.addTag,
  AutomationPipelineType.forwardReply,
  AutomationPipelineType.unsubscribe,
  AutomationPipelineType.sendSms,
]

const defaultPipelineTypeOptions = pipelineType.map((el) => ({
  label: el,
  value: el,
}))

const toOptions = [
  {label: 'sender', value: 'sender'},
  {label: 'custom', value: 'custom'},
]

type PipelineItemProps = {
  automation: Automation
  data: AutomationPipeline
  index: number
  tagOptions: SelectOption<string>[]
  senderOptions: SelectOption<string>[]
  inputError: Record<string, any>
  handleChange: (index: number, key: string, newValue: any) => void
  handleChangeConfig: (index: number, key: string, newValue: any) => void
  removeByIndex: (index: number) => void
}
const PipelineItem: FC<PipelineItemProps> = (props) => {
  const {
    automation,
    data,
    index,
    tagOptions,
    senderOptions,
    inputError: error,
    handleChange,
    handleChangeConfig,
    removeByIndex,
  } = props

  const [pipelineTypeOptions, SetPipelineTypeOptions] = useState<
    {
      label: AutomationPipelineType
      value: AutomationPipelineType
    }[]
  >(defaultPipelineTypeOptions)

  useEffect(() => {
    if(automation.trigger !== 'replyMessage') {
      SetPipelineTypeOptions(pipelineTypeOptions.filter((el) => el.value !== AutomationPipelineType.forwardReply))
    } else {
      SetPipelineTypeOptions(defaultPipelineTypeOptions)
    }
  }, [automation.trigger])

  return (
    <div className='pipeline-step container'>
      <div className='row col-12'>
        <label
          className='form-label'
          style={{
            paddingLeft: 0,
            marginLeft: 0,
          }}
        >
          Action {index + 1}
        </label>
      </div>
      <div className='row col-12'>
        <label
          className='form-label mt-5'
          style={{
            paddingLeft: 0,
            marginLeft: 0,
          }}
        >
          Type
        </label>
        <Select
          styles={{
            container: (provided) => ({
              ...provided,
              paddingLeft: 0,
              paddingRight: 0,
            }),
          }}
          onChange={(e: any) => {
            const pipelineType = e.value as any
            handleChange(index, 'type', pipelineType)
          }}
          value={pipelineTypeOptions.find((el) => el.value === data.type) || pipelineTypeOptions[0]}
          isMulti={false}
          options={pipelineTypeOptions as any}
        />
        {error.type && <span className='text-danger mt-2'>{error.type}</span>}
      </div>
      {data.type === AutomationPipelineType.addTag && (
        <div className='row col-12'>
          <label
            className='form-label mt-5'
            style={{
              paddingLeft: 0,
              marginLeft: 0,
            }}
          >
            Tags
          </label>
          <Select
            styles={{
              container: (provided) => ({
                ...provided,
                paddingLeft: 0,
                paddingRight: 0,
              }),
            }}
            onChange={(e: any) => {
              let tagIds = []
              if (Array.isArray(e)) {
                tagIds = e.flatMap((e) => e.value)
              }
              handleChangeConfig(index, 'tagIds', tagIds)
            }}
            value={
              tagOptions.filter(
                (el) => Array.isArray(data.config?.tagIds) && data.config?.tagIds.includes(el.value)
              ) || []
            }
            isMulti={true}
            options={tagOptions as any}
          />
          {error.tagIds && <span className='text-danger mt-2'>{error.tagIds}</span>}
        </div>
      )}

      {data.type === AutomationPipelineType.forwardReply && (
        <div className='row col-12'>
          <label
            className='form-label mt-5'
            style={{
              paddingLeft: 0,
              marginLeft: 0,
            }}
          >
            Email
          </label>
          <input
            onChange={(e) => handleChangeConfig(index, 'email', e.target.value)}
            type='text'
            className='form-control form-control-solid bg-light border-0'
            placeholder='targetEmail1@mail.com,targetEmail2@mail.com'
            value={data.config?.email || ''}
          />
          {error.email && <span className='text-danger mt-2'>{error.email}</span>}
        </div>
      )}

      {data.type === AutomationPipelineType.sendSms && (
        <div className='row col-12'>
          <label
            className='form-label mt-5'
            style={{
              paddingLeft: 0,
              marginLeft: 0,
            }}
          >
            To
          </label>
          <Select
            styles={{
              container: (provided) => ({
                ...provided,
                paddingLeft: 0,
                paddingRight: 0,
              }),
            }}
            onChange={(e: any) => {
              handleChangeConfig(index, 'to', e.value)
            }}
            value={toOptions.find((el) => el.value === data.config?.to) || toOptions[0]}
            options={toOptions as any}
          />
          {error.to && <span className='text-danger mt-2'>{error.to}</span>}
        </div>
      )}

      {data.type === AutomationPipelineType.sendSms && (
        <div className='row col-12'>
          <label
            className='form-label mt-5'
            style={{
              paddingLeft: 0,
              marginLeft: 0,
            }}
          >
            From
          </label>
          <Select
            styles={{
              container: (provided) => ({
                ...provided,
                paddingLeft: 0,
                paddingRight: 0,
              }),
            }}
            onChange={(e: any) => {
              handleChangeConfig(index, 'from', e.value)
            }}
            value={senderOptions.find((el) => el.value === data.config?.from) || senderOptions[0]}
            options={senderOptions as any}
          />
          {error.from && <span className='text-danger mt-2'>{error.from}</span>}
        </div>
      )}

      {data.type === AutomationPipelineType.sendSms &&
        data.config &&
        data.config.to &&
        data.config.to === 'custom' && (
          <div className='row col-12'>
            <label
              className='form-label mt-5'
              style={{
                paddingLeft: 0,
                marginLeft: 0,
              }}
            >
              Recipients
            </label>
            <input
              onChange={(e) => handleChangeConfig(index, 'recipients', e.target.value)}
              type='text'
              className='form-control form-control-solid bg-light border-0'
              placeholder='400000000,410000000'
              value={data.config?.recipients?.join(',') || ''}
            />
            {error.to && <span className='text-danger mt-2'>{error.to}</span>}
          </div>
        )}

      {data.type === AutomationPipelineType.sendSms && (
        <div className='row col-12 m-0 p-0 mt-5'>
          <MessageBoxV2
            setError={(err: any) => {}}
            error={error}
            setUserMessage={(payload: any) => {
              const {message} = payload
              handleChangeConfig(index, 'message', message)
            }}
            userMessage={data.config || {message: ''}}
            options={[]}
            quickSms={true}
            showSelect={['customfield']}
            itQuickMessage={true}
            containerStyle={{paddingLeft: 0, marginRight: 0}}
          />
          {error.message && <span className='text-danger mt-2'>{error.message}</span>}
        </div>
      )}

      {index !== 0 && (
        <div className='row col-12 d-flex mt-5 justify-content-end'>
          <button
            onClick={() => removeByIndex(index)}
            className='btn btn-danger btn-sm'
            style={{width: 110}}
          >
            <i className='bi bi-trash'></i> Remove
          </button>
        </div>
      )}
    </div>
  )
}

export default InputPipeline
